/**
 * TourOverlay — the spotlight + tooltip card for the active feature tour.
 *
 * Rendered once (sibling of the routes in AppRouter) via a portal to
 * document.body. Layering: container z 10000 / card z 10001 — above Chakra
 * modals (~1400), portal menus (1500) and the FutureBot widget (9998/9999),
 * so steps can highlight elements anywhere, including inside modals.
 *
 * Structure:
 *   - spotlight div: sits on the padded target rect; a huge box-shadow dims
 *     everything EXCEPT the target (which therefore reads as brighter) plus a
 *     blue focus ring. pointer-events: none.
 *   - 4 click shields around the spotlight (plus a 5th over the target when
 *     the step is not interactive) blocking stray clicks — this also protects
 *     closeOnOverlayClick modals underneath.
 *   - tooltip card: positioned next to the spotlight by computeCardPosition.
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Button,
  CloseButton,
  Flex,
  HStack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useFeatureTour } from './FeatureTourProvider';
import {
  padRect,
  rectsRoughlyEqual,
  isElementVisible,
  computeCardPosition,
} from './targetUtils';

const OVERLAY_Z = 10000;
const CARD_Z = 10001;
const DETACH_GRACE_MS = 1000;

const stopEvent = (e) => {
  e.stopPropagation();
  e.preventDefault();
};

export default function TourOverlay() {
  const { activeTour, stepIndex, targetEl, next, back, endTour } = useFeatureTour();

  const step = activeTour && stepIndex >= 0 ? (activeTour.steps || [])[stepIndex] : null;
  const totalSteps = activeTour ? (activeTour.steps || []).length : 0;
  const padding = step && step.spotlightPadding != null ? step.spotlightPadding : 8;
  // advanceOn: 'click' implies the target must be clickable.
  const isInteractive = !!(step && (step.interactive || step.advanceOn === 'click'));
  const isLastStep = stepIndex >= totalSteps - 1;

  // ---- hooks (all before any early return — react-hooks/rules-of-hooks) ----
  const dimColor = useColorModeValue('rgba(23, 25, 35, 0.55)', 'rgba(0, 0, 0, 0.7)');
  const ringColor = useColorModeValue('rgba(66, 153, 225, 0.6)', 'rgba(99, 179, 237, 0.6)');
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.600');
  const bodyColor = useColorModeValue('gray.600', 'gray.300');
  const counterColor = useColorModeValue('blue.500', 'blue.300');

  const [targetRect, setTargetRect] = useState(null);
  const [cardPos, setCardPos] = useState(null);
  const cardRef = useRef(null);

  // Fresh handlers for DOM listeners without re-subscribing.
  const nextRef = useRef(next);
  nextRef.current = next;
  const backRef = useRef(back);
  backRef.current = back;
  const endTourRef = useRef(endTour);
  endTourRef.current = endTour;

  // Track the target's rect: resize + capture-phase scroll (nested scrollers),
  // ResizeObserver, and a 300ms interval safety net (layout shifts, detach
  // detection). If the target disappears for >1s (modal closed manually,
  // list re-rendered), auto-advance to the next step.
  useEffect(() => {
    if (!targetEl) {
      setTargetRect(null);
      return undefined;
    }
    let frame = null;
    let detachedSince = null;
    let disposed = false;

    const measure = () => {
      frame = null;
      if (disposed) return;
      if (!isElementVisible(targetEl)) {
        if (detachedSince == null) {
          detachedSince = Date.now();
        } else if (Date.now() - detachedSince > DETACH_GRACE_MS) {
          detachedSince = null;
          nextRef.current();
        }
        return;
      }
      detachedSince = null;
      const r = targetEl.getBoundingClientRect();
      const rect = { top: r.top, left: r.left, width: r.width, height: r.height };
      setTargetRect((prev) => (rectsRoughlyEqual(prev, rect) ? prev : rect));
    };

    const schedule = () => {
      if (frame == null && !disposed) frame = requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(schedule);
      resizeObserver.observe(targetEl);
    }
    const interval = setInterval(schedule, 300);

    return () => {
      disposed = true;
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
      if (resizeObserver) resizeObserver.disconnect();
      clearInterval(interval);
      if (frame != null) cancelAnimationFrame(frame);
    };
  }, [targetEl]);

  // Position the card next to the spotlight once its size is measurable.
  useLayoutEffect(() => {
    if (!targetRect || !step || !cardRef.current) {
      setCardPos(null);
      return;
    }
    const size = { width: cardRef.current.offsetWidth, height: cardRef.current.offsetHeight };
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    setCardPos(
      computeCardPosition(padRect(targetRect, padding), size, viewport, step.placement || 'auto')
    );
  }, [targetRect, step, padding]);

  // Keyboard: Esc dismisses (capture-phase + stopPropagation so a Chakra
  // modal underneath doesn't also close); arrows navigate unless typing.
  useEffect(() => {
    if (!activeTour) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        endTourRef.current('dismissed');
        return;
      }
      const tag = e.target && e.target.tagName;
      const isTyping = tag === 'INPUT' || tag === 'TEXTAREA' || (e.target && e.target.isContentEditable);
      if (isTyping) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        e.stopPropagation();
        nextRef.current();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        e.stopPropagation();
        backRef.current();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [activeTour]);

  // advanceOn: 'click' — one-time capture listener on the target; small delay
  // lets the app's own onClick run first (e.g. opening a modal).
  useEffect(() => {
    if (!targetEl || !step || step.advanceOn !== 'click') return undefined;
    const handler = () => {
      setTimeout(() => nextRef.current(), 150);
    };
    targetEl.addEventListener('click', handler, { capture: true, once: true });
    return () => targetEl.removeEventListener('click', handler, { capture: true });
  }, [targetEl, step]);

  // ---- render ----
  if (!activeTour || !step || !targetRect) return null;

  const spot = padRect(targetRect, padding);
  const spotBottom = spot.top + spot.height;
  const spotRight = spot.left + spot.width;

  const shieldBase = {
    position: 'fixed',
    background: 'transparent',
    pointerEvents: 'auto',
    zIndex: OVERLAY_Z,
  };

  return createPortal(
    <div
      data-feature-tour-overlay=""
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: OVERLAY_Z,
        pointerEvents: 'none',
      }}
    >
      {/* Spotlight: the giant shadow dims everything except the target. */}
      <div
        style={{
          position: 'fixed',
          top: spot.top,
          left: spot.left,
          width: spot.width,
          height: spot.height,
          borderRadius: 8,
          boxShadow: `0 0 0 4px ${ringColor}, 0 0 0 100vmax ${dimColor}`,
          transition: 'top .25s ease, left .25s ease, width .25s ease, height .25s ease',
          pointerEvents: 'none',
        }}
      />

      {/* Click shields around (and optionally over) the spotlight. */}
      <div
        style={{ ...shieldBase, top: 0, left: 0, right: 0, height: Math.max(spot.top, 0) }}
        onClick={stopEvent}
        onMouseDown={stopEvent}
      />
      <div
        style={{ ...shieldBase, top: Math.max(spotBottom, 0), left: 0, right: 0, bottom: 0 }}
        onClick={stopEvent}
        onMouseDown={stopEvent}
      />
      <div
        style={{
          ...shieldBase,
          top: Math.max(spot.top, 0),
          left: 0,
          width: Math.max(spot.left, 0),
          height: Math.max(spot.height, 0),
        }}
        onClick={stopEvent}
        onMouseDown={stopEvent}
      />
      <div
        style={{
          ...shieldBase,
          top: Math.max(spot.top, 0),
          left: spotRight,
          right: 0,
          height: Math.max(spot.height, 0),
        }}
        onClick={stopEvent}
        onMouseDown={stopEvent}
      />
      {!isInteractive && (
        <div
          style={{
            ...shieldBase,
            top: spot.top,
            left: spot.left,
            width: spot.width,
            height: spot.height,
            cursor: 'default',
          }}
          onClick={stopEvent}
          onMouseDown={stopEvent}
        />
      )}

      {/* Tooltip card */}
      <Box
        ref={cardRef}
        position="fixed"
        zIndex={CARD_Z}
        w="min(360px, calc(100vw - 32px))"
        bg={cardBg}
        borderWidth="1px"
        borderColor={cardBorder}
        borderRadius="lg"
        boxShadow="2xl"
        p={4}
        role="dialog"
        aria-label={step.title || 'Feature tour'}
        style={{
          top: cardPos ? cardPos.top : -9999,
          left: cardPos ? cardPos.left : -9999,
          visibility: cardPos ? 'visible' : 'hidden',
          pointerEvents: 'auto',
        }}
      >
        <Flex justify="space-between" align="center" mb={2}>
          <Text
            fontSize="xs"
            fontWeight="bold"
            textTransform="uppercase"
            letterSpacing="wide"
            color={counterColor}
          >
            Step {stepIndex + 1} of {totalSteps}
          </Text>
          <CloseButton size="sm" onClick={() => endTour('dismissed')} aria-label="Close tour" />
        </Flex>
        {step.title && (
          <Text fontWeight="semibold" fontSize="md" mb={1}>
            {step.title}
          </Text>
        )}
        {step.body && (
          <Text fontSize="sm" color={bodyColor} mb={4}>
            {step.body}
          </Text>
        )}
        <Flex justify="space-between" align="center">
          <Button variant="link" size="xs" colorScheme="gray" onClick={() => endTour('dismissed')}>
            Skip tour
          </Button>
          <HStack spacing={2}>
            {stepIndex > 0 && (
              <Button size="sm" variant="ghost" onClick={back}>
                Back
              </Button>
            )}
            <Button size="sm" colorScheme="blue" onClick={next}>
              {isLastStep ? 'Done' : 'Next'}
            </Button>
          </HStack>
        </Flex>
      </Box>
    </div>,
    document.body
  );
}
